/**
 * ApprovalMatrix.js
 * Approval workflow for high-impact patch actions.
 * Roles:
 *   admin    — can approve/reject all requests, auto-executes on approval
 *   operator — can submit requests, view their own status
 *   viewer   — read-only view of approval history
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const STATUS_STYLE = {
  pending:   { bg: '#fef3c7', color: '#d97706', label: '⏳ Pending' },
  approved:  { bg: '#dcfce7', color: '#16a34a', label: '✔ Approved' },
  rejected:  { bg: '#fee2e2', color: '#dc2626', label: '✘ Rejected' },
  executed:  { bg: '#dbeafe', color: '#1d4ed8', label: '⚡ Executed' },
  expired:   { bg: '#f1f5f9', color: '#94a3b8', label: '⌛ Expired' },
  cancelled: { bg: '#f1f5f9', color: '#94a3b8', label: '✕ Cancelled' },
};

const PRIORITY_STYLE = {
  critical: { bg: '#fee2e2', color: '#dc2626' },
  high:     { bg: '#ffedd5', color: '#ea580c' },
  medium:   { bg: '#fef3c7', color: '#d97706' },
  low:      { bg: '#f0fdf4', color: '#16a34a' },
};

const ACTION_ICONS = {
  emergency_patch: '🚨',
  enforce_patches: '⚡',
  rollback:        '↩',
  deploy_patch:    '📦',
};

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function timeLeft(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - Date.now();
  if (diff <= 0) return { label: 'Request expired — no longer actionable', urgent: true };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const timeStr = h > 0 ? `${h}h ${m}m` : `${m}m`;
  if (diff < 3600000)  return { label: `⚠ Expires in ${timeStr} — action needed soon`, urgent: true };
  if (diff < 7200000)  return { label: `Expires in ${timeStr} — review before it lapses`, urgent: false };
  return { label: `Expires in ${timeStr}`, urgent: false };
}

export default function ApprovalMatrix({ user }) {
  const [requests, setRequests] = useState([]);
  const [matrix,   setMatrix]   = useState({});
  const [summary,  setSummary]  = useState({});
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('pending');
  const [msg,      setMsg]      = useState({ text: '', type: '' });

  // Create request modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    action: 'emergency_patch', priority: 'high', justification: '',
    patchName: '', deviceNames: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Review modal
  const [reviewReq, setReviewReq]   = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewing,  setReviewing]  = useState(false);

  const isAdmin    = user?.role === 'admin';
  const isOperator = user?.role === 'operator' || user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, mRes] = await Promise.all([
        api.getApprovals(),
        api.getApprovalMatrix()
      ]);
      setRequests(aRes.data.requests || []);
      setSummary(aRes.data.summary || {});
      setMatrix(mRes.data || {});
    } catch (e) {
      flash('Failed to load: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 5000);
  }

  async function submitRequest() {
    setSubmitting(true);
    try {
      const deviceNames = createForm.deviceNames
        ? createForm.deviceNames.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      await api.createApproval({
        action:       createForm.action,
        priority:     createForm.priority,
        justification: createForm.justification,
        actionPayload: {
          patchName:   createForm.patchName,
          deviceNames,
        }
      });
      flash('Approval request submitted successfully');
      setShowCreate(false);
      setCreateForm({ action: 'emergency_patch', priority: 'high', justification: '', patchName: '', deviceNames: '' });
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function doApprove() {
    if (!reviewReq) return;
    setReviewing(true);
    try {
      await api.approveRequest(reviewReq.id, { reviewNote, executeNow: true });
      flash(`✔ Request approved and executed`);
      setReviewReq(null);
      setReviewNote('');
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setReviewing(false);
    }
  }

  async function doReject() {
    if (!reviewReq) return;
    setReviewing(true);
    try {
      await api.rejectRequest(reviewReq.id, { reviewNote });
      flash('Request rejected');
      setReviewReq(null);
      setReviewNote('');
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setReviewing(false);
    }
  }

  async function doCancel(id) {
    try {
      await api.cancelRequest(id);
      flash('Request cancelled');
      await load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    }
  }

  // Filter requests by tab
  const filtered = requests.filter(r => {
    if (tab === 'pending')  return r.status === 'pending';
    if (tab === 'history')  return ['approved','rejected','executed','expired','cancelled'].includes(r.status);
    return true;
  });

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">✅ Approval Matrix</div>
          <div className="page-subtitle">
            Role-based approval workflow for high-impact patch actions
          </div>
        </div>
        <div className="flex-gap">
          <button className="refresh-btn" onClick={load} disabled={loading}>↻ Refresh</button>
          {isOperator && (
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              + New Request
            </button>
          )}
        </div>
      </div>

      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}

      {/* KPI cards */}
      <div className="cards-row" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 20 }}>
        {[
          { label: 'Pending',  value: summary.pending  || 0, cls: 'amber' },
          { label: 'Approved', value: summary.approved || 0, cls: 'green' },
          { label: 'Executed', value: summary.executed || 0, cls: 'blue'  },
          { label: 'Rejected', value: summary.rejected || 0, cls: 'red'   },
          { label: 'Total',    value: summary.total    || 0, cls: 'cyan'  },
        ].map(c => (
          <div key={c.label} className={`card ${c.cls}`}>
            <div className="card-label">{c.label}</div>
            <div className="card-value">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Approval Matrix reference */}
      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="panel-header">
          <div className="panel-title">
            <div className="panel-title-icon">⚙</div>
            Approval Matrix — Action Requirements
          </div>
        </div>
        <div style={{ padding: '12px 20px' }}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
            Requests automatically expire if not reviewed within the time limit below.
            An expired request cannot be approved — the requester must submit a new one.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {Object.entries(matrix).map(([key, cfg]) => (
              <div key={key} style={{
                background: '#f8fafc', border: '1px solid #e8ecf4',
                borderRadius: 8, padding: '10px 14px', minWidth: 200
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1e2a3b', marginBottom: 6 }}>
                  {cfg.icon} {cfg.label}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {cfg.requiresApproval ? (
                    <span className="badge pending" style={{ width: 'fit-content' }}>
                      Requires Admin Approval
                    </span>
                  ) : (
                    <span className="badge success" style={{ width: 'fit-content' }}>
                      Admin: Auto-Execute
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    ⏱ Review window: <strong>{cfg.defaultExpiry} hours</strong>
                  </span>
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>
                    {cfg.defaultExpiry <= 4
                      ? 'Critical — must be reviewed within ' + cfg.defaultExpiry + 'h or request lapses'
                      : 'Request lapses after ' + cfg.defaultExpiry + 'h if not reviewed'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e8ecf4', marginBottom: 20 }}>
        {[
          { id: 'pending', label: `⏳ Pending (${summary.pending || 0})` },
          { id: 'history', label: `📋 History` },
          { id: 'all',     label: `All Requests (${summary.total || 0})` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? '#1a56db' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #1a56db' : '2px solid transparent',
            marginBottom: -2
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Request list */}
      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">
            <div className="panel-title-icon">✅</div>
            {tab === 'pending' ? 'Pending Approvals' : tab === 'history' ? 'Approval History' : 'All Requests'}
          </div>
        </div>
        {loading ? <div className="loading">Loading...</div> : (
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>Request</th>
                <th>Requested By</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Reviewed By</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="empty">
                  {tab === 'pending' ? 'No pending approvals — all clear ✔' : 'No requests found.'}
                </td></tr>
              )}
              {filtered.map(r => {
                const sts = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                const pri = PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.medium;
                const tl  = r.status === 'pending' ? timeLeft(r.expiresAt) : null;
                return (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontSize: 16, textAlign: 'center' }}>
                        {ACTION_ICONS[r.action] || '📋'}
                      </div>
                    </td>
                    <td style={{ maxWidth: 260 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1e2a3b' }}>
                        {r.actionLabel}
                      </div>
                      {r.justification && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                          {r.justification.substring(0, 80)}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        {fmtDateTime(r.requestedAt)}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{r.requestedBy}</div>
                    </td>
                    <td>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px',
                        borderRadius: 12, background: pri.bg, color: pri.color }}>
                        {r.priority}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px',
                        borderRadius: 20, background: sts.bg, color: sts.color }}>
                        {sts.label}
                      </span>
                      {tl && (
                        <div style={{ fontSize: 10,
                          color: tl.urgent ? '#dc2626' : '#94a3b8',
                          marginTop: 2 }}>
                          {tl.label}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>
                      {r.reviewedBy || '—'}
                      {r.reviewNote && (
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>
                          {r.reviewNote.substring(0, 40)}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 11, color: '#94a3b8' }}>
                      {fmtDateTime(r.expiresAt)}
                    </td>
                    <td>
                      <div className="flex-gap">
                        {/* Admin: approve/reject pending */}
                        {isAdmin && r.status === 'pending' && (
                          <>
                            <button className="btn btn-sm btn-primary"
                              onClick={() => { setReviewReq(r); setReviewNote(''); }}>
                              Review
                            </button>
                          </>
                        )}
                        {/* Requester: cancel own pending request */}
                        {r.status === 'pending' && (r.requestedBy === user?.username || isAdmin) && (
                          <button className="btn btn-sm btn-ghost"
                            onClick={() => doCancel(r.id)}>
                            Cancel
                          </button>
                        )}
                        {/* Executed: show result */}
                        {r.status === 'executed' && r.executionResult && (
                          <span style={{ fontSize: 10, color: '#16a34a' }}>
                            ✔ Done
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Create Request Modal ── */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 500,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2a3b', marginBottom: 20 }}>
              New Approval Request
            </div>

            <div className="form-group">
              <label className="form-label">Action</label>
              <select className="form-input"
                value={createForm.action}
                onChange={e => setCreateForm(f => ({ ...f, action: e.target.value }))}>
                {Object.entries(matrix).map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.icon} {cfg.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Patch / Target (optional)</label>
              <input className="form-input" placeholder="e.g. Chrome Update, Windows 11"
                value={createForm.patchName}
                onChange={e => setCreateForm(f => ({ ...f, patchName: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Target Devices (comma-separated, optional)</label>
              <input className="form-input" placeholder="e.g. LP000113, LP000114"
                value={createForm.deviceNames}
                onChange={e => setCreateForm(f => ({ ...f, deviceNames: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-input"
                value={createForm.priority}
                onChange={e => setCreateForm(f => ({ ...f, priority: e.target.value }))}>
                <option value="critical">🔴 Critical — immediate action required</option>
                <option value="high">🟠 High — action required today</option>
                <option value="medium">🟡 Medium — within this week</option>
                <option value="low">🟢 Low — scheduled maintenance</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Justification *</label>
              <textarea className="form-input" rows={3}
                placeholder="Explain why this action is needed..."
                value={createForm.justification}
                onChange={e => setCreateForm(f => ({ ...f, justification: e.target.value }))} />
            </div>

            <div className="flex-gap" style={{ justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitRequest}
                disabled={submitting || !createForm.justification.trim()}>
                {submitting ? '⟳ Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Review Modal (admin) ── */}
      {reviewReq && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 500,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2a3b', marginBottom: 4 }}>
              Review Approval Request
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>
              Requested by {reviewReq.requestedBy} · {fmtDateTime(reviewReq.requestedAt)}
            </div>

            {/* Request summary */}
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
                {ACTION_ICONS[reviewReq.action]} {reviewReq.actionLabel}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
                  background: PRIORITY_STYLE[reviewReq.priority]?.bg,
                  color: PRIORITY_STYLE[reviewReq.priority]?.color }}>
                  {reviewReq.priority} priority
                </span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  expires {fmtDateTime(reviewReq.expiresAt)}
                </span>
              </div>
              {reviewReq.justification && (
                <div style={{ fontSize: 13, color: '#374151' }}>
                  <strong>Justification:</strong> {reviewReq.justification}
                </div>
              )}
            </div>

            <div className="alert info" style={{ marginBottom: 16 }}>
              ⚡ Approving will <strong>immediately execute</strong> this action on the target devices.
            </div>

            <div className="form-group">
              <label className="form-label">Review Notes (optional)</label>
              <textarea className="form-input" rows={2}
                placeholder="Add comments for the requester..."
                value={reviewNote}
                onChange={e => setReviewNote(e.target.value)} />
            </div>

            <div className="flex-gap" style={{ justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={() => setReviewReq(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={doReject} disabled={reviewing}>
                {reviewing ? '⟳' : '✘ Reject'}
              </button>
              <button className="btn btn-primary" onClick={doApprove} disabled={reviewing}
                style={{ background: '#16a34a' }}>
                {reviewing ? '⟳ Processing...' : '✔ Approve & Execute'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
